import Vue from 'vue';
import { mapState } from 'vuex';
import 'viewerjs/dist/viewer.css';
import VueViewer from 'v-viewer';
Vue.use(VueViewer);
export default Vue.extend({
    props: {
        showThumbnails: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            selectedMediaIndex: 0,
            isLeftDisabled: true,
            isRightDisabled: false,
        };
    },
    computed: {
        ...mapState('productModule', [
            'currentProduct',
            'currentVariant',
        ]),
        product() {
            return this.currentProduct;
        },
        /**
         * Variant media remains image-only.
         *
         * When the selected variant has its own images, keep the existing
         * behaviour and show those images instead of product-level media.
         */
        galleryItems() {
            const productMedia = Array.isArray(this.currentProduct?.media)
                ? this.currentProduct.media
                : Array.isArray(this.currentProduct?.content?.media)
                    ? this.currentProduct.content.media
                    : Array.isArray(this.currentProduct?.images)
                        ? this.currentProduct.images
                        : [];
            if (productMedia.length) {
                return productMedia;
            }
            const variantImages = Array.isArray(this.currentVariant?.images)
                ? this.currentVariant.images
                : [];
            return variantImages.map((item) => ({
                ...item,
                media_type: 'image',
                mime_type: item.mime_type || 'image/*',
            }));
        },
        selectedMediaItem() {
            return (this.galleryItems[this.selectedMediaIndex] ||
                this.galleryItems[0] ||
                null);
        },
        selectedItemIsVideo() {
            return this.isVideo(this.selectedMediaItem);
        },
        imageViewerItems() {
            return this.galleryItems.filter(item => !this.isVideo(item));
        },
        selectedImageViewerIndex() {
            if (!this.selectedMediaItem || this.selectedItemIsVideo) {
                return 0;
            }
            const index = this.imageViewerItems.findIndex(item => {
                if (this.selectedMediaItem?.id &&
                    item.id) {
                    return item.id === this.selectedMediaItem.id;
                }
                return item.url === this.selectedMediaItem?.url;
            });
            return index >= 0 ? index : 0;
        },
    },
    created() {
        this.resetSelectedMedia();
    },
    mounted() {
        this.$nextTick(() => {
            this.installGalleryListeners();
            this.checkScrollButtons();
        });
    },
    beforeDestroy() {
        this.removeGalleryListeners();
    },
    methods: {
        isVideo(item) {
            if (!item)
                return false;
            return (item.media_type === 'video' ||
                String(item.mime_type || '').startsWith('video/'));
        },
        resetSelectedMedia() {
            this.selectedMediaIndex = 0;
        },
        onThumbnailSelect(item, index) {
            this.selectedMediaIndex = index;
        },
        showImageViewer() {
            if (this.selectedItemIsVideo ||
                !this.imageViewerItems.length) {
                return;
            }
            this.$viewerApi({
                options: {
                    className: 'img-preview',
                    toolbar: true,
                    url: 'url',
                    initialViewIndex: this.selectedImageViewerIndex,
                    slideOnTouch: true,
                    zoomOnTouch: true,
                    zoomOnWheel: true,
                    toggleOnDblclick: false,
                },
                images: this.imageViewerItems,
            });
        },
        getScrollContainer() {
            return (this.$refs.scrollContainer ||
                null);
        },
        installGalleryListeners() {
            const container = this.getScrollContainer();
            if (!container)
                return;
            container.addEventListener('scroll', this.checkScrollButtons);
            window.addEventListener('resize', this.checkScrollButtons);
            const previewElements = container.querySelectorAll('img, video');
            if (!previewElements.length) {
                this.checkScrollButtons();
                return;
            }
            let loadedCount = 0;
            const checkLoaded = () => {
                loadedCount += 1;
                if (loadedCount >= previewElements.length) {
                    this.$nextTick(() => {
                        this.checkScrollButtons();
                    });
                }
            };
            previewElements.forEach(element => {
                if (element instanceof HTMLImageElement) {
                    if (element.complete) {
                        checkLoaded();
                    }
                    else {
                        element.addEventListener('load', checkLoaded, { once: true });
                        element.addEventListener('error', checkLoaded, { once: true });
                    }
                    return;
                }
                if (element instanceof HTMLVideoElement) {
                    if (element.readyState >= 1) {
                        checkLoaded();
                    }
                    else {
                        element.addEventListener('loadedmetadata', checkLoaded, { once: true });
                        element.addEventListener('error', checkLoaded, { once: true });
                    }
                }
            });
        },
        removeGalleryListeners() {
            const container = this.getScrollContainer();
            if (container) {
                container.removeEventListener('scroll', this.checkScrollButtons);
            }
            window.removeEventListener('resize', this.checkScrollButtons);
        },
        scrollLeft() {
            const container = this.getScrollContainer();
            if (!container)
                return;
            container.scrollBy({
                left: -(container.clientWidth * 0.8),
                behavior: 'smooth',
            });
            window.setTimeout(this.checkScrollButtons, 300);
        },
        scrollRight() {
            const container = this.getScrollContainer();
            if (!container)
                return;
            container.scrollBy({
                left: container.clientWidth * 0.8,
                behavior: 'smooth',
            });
            window.setTimeout(this.checkScrollButtons, 300);
        },
        checkScrollButtons() {
            const container = this.getScrollContainer();
            if (!container) {
                this.isLeftDisabled = true;
                this.isRightDisabled = true;
                return;
            }
            const maxScrollLeft = container.scrollWidth -
                container.clientWidth;
            this.isLeftDisabled =
                container.scrollLeft <= 1;
            this.isRightDisabled =
                maxScrollLeft <= 1 ||
                    container.scrollLeft >= maxScrollLeft - 1;
        },
    },
    watch: {
        currentVariant() {
            this.resetSelectedMedia();
            this.$nextTick(() => {
                this.removeGalleryListeners();
                this.installGalleryListeners();
                this.checkScrollButtons();
            });
        },
        galleryItems() {
            this.resetSelectedMedia();
            this.$nextTick(() => {
                this.removeGalleryListeners();
                this.installGalleryListeners();
                this.checkScrollButtons();
            });
        },
    },
});
